// src/pages/JoinLeague.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import {
  setDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  arrayUnion,
  doc,
  getDoc
} from "firebase/firestore";
import { db } from "../lib/firebase";
import Layout from "../central/Layout";
import Button from "../central/Button";
import Panel from "../central/Panel";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { ref } from "../lib/fw";
import { paths } from "../lib/paths";
import { v4 as uuidv4 } from "uuid";
import { logError } from "../utils/logError";
import { getLeagueConfig } from "../utils/leagueConfig";
import { setActiveSeasonID } from "../lib/seasonPaths";
import { logEvent } from "../utils/EventLogger";

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: ${({ theme }) => theme.spacing.xxlarge};
  min-height: calc(100vh - 80px);
  background-color: ${({ theme }) => theme.colors.background};
`;

const FormPanel = styled(Panel)`
  width: 100%;
  max-width: 420px;
  padding: ${({ theme }) => theme.spacing.xlarge};
  background-color: white;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const StyledLabel = styled.label`
  font-weight: 500;
  color: #333;
  margin-bottom: 0.3rem;
`;

const StyledInput = styled.input`
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.6rem 0.8rem;
  width: 100%;
  font-size: 0.95rem;
  background-color: #f9fafb;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }
`;


export default function JoinLeague() {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();
  const [code, setCode] = useState("");
  const [teamName, setTeamName] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState("join"); // "join" | "claim"
  const [leaguePlayers, setLeaguePlayers] = useState([]);
  const [joinedLeagueID, setJoinedLeagueID] = useState(null);
  const [selectedPlayerID, setSelectedPlayerID] = useState("");
  const [savingClaim, setSavingClaim] = useState(false);


  useEffect(() => {
    const codeFromUrl = new URLSearchParams(location.search).get("code");
    if (codeFromUrl) {
      setCode(codeFromUrl.trim());
    }
  }, [location.search]);

  const handleJoin = async () => {
    try {
      if (!currentUser) {
        showToast("You must be signed in.", "error");
        return;
      }
      // Validation: require teamName (simulate initial squad selection requirement)
      if (!teamName.trim()) {
        showToast("Please enter a team name.", "error");
        return;
      }

      // Query leagues collection by code field
      const leaguesRef = collection(db, "leagues");
      const q = query(leaguesRef, where("code", "==", code));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        await logError({
          leagueID: "N/A",
          userID: currentUser?.uid,
          action: "handleJoin_invalidCode",
          payload: { code },
          error: "Invalid league code"
        });
        showToast("Invalid league code.", "error");
        return;
      }
      const leagueDoc = querySnapshot.docs[0];
      const leagueID = leagueDoc.id;
      const leagueData = leagueDoc.data();
      console.log("Found league:", leagueID, leagueData);

      // Use explicit seasonID for all refs in this flow (do not rely on global active season)
      const seasonID = leagueData.currentSeasonID || "S1";
      setActiveSeasonID(seasonID);

      // 🚫 Prevent joining the same league twice (season-scoped)
      const teamsRef = collection(db, "leagues", leagueID, "seasons", seasonID, "teams");
      const existingTeamQ = query(teamsRef, where("managerID", "==", currentUser.uid));
      const existingTeamSnap = await getDocs(existingTeamQ);
      if (!existingTeamSnap.empty) {
        showToast("You are already in this league.", "error");
        return;
      }
      const teamID = uuidv4();
      const leagueType = leagueData.leagueType || leagueData.leagueType;

      const leagueConfig = getLeagueConfig(leagueType);
      const startingBudget = leagueConfig.budget;

      // Query the league's gameweeks subcollection for the current gameweek
      const gameweeksRef = collection(db, "leagues", leagueID, "seasons", seasonID, "gameweeks");
      const currentGWQuery = query(gameweeksRef, where("isCurrent", "==", true));
      const currentGWSnapshot = await getDocs(currentGWQuery);
      console.log("Current GW snapshot:", currentGWSnapshot.docs.map(d => ({id: d.id, data: d.data()})));

      let currentGWID = null;
      if (!currentGWSnapshot.empty) {
        const currentGWDoc = currentGWSnapshot.docs[0];
        currentGWID = currentGWDoc.id;
      }

      // Create team doc at /leagues/{leagueID}/seasons/{seasonID}/teams/{teamID} with full schema in a single setDoc call
      console.log("League teams path:", teamsRef.path);
      const teamRef = doc(teamsRef, teamID);
      const teamDocPayload = {
        managerID: currentUser.uid,
        leagueID,
        leagueType,
        name: teamName.trim() || "My Team",
        createdAt: serverTimestamp(),

        captains: {},
        chipsUsed: {},
        selections: {},

        transfersMade: { [currentGWID]: 0 },
        transfersFree: { [currentGWID]: "unlimited" },

        startGW: currentGWID,

        bankStart: { [currentGWID]: startingBudget },
        currentBudget: { [currentGWID]: startingBudget },

        squadMeta: {
          [currentGWID]: {
            DEF: {},
            MID: {},
            FWD: {}
          }
        }
      };
      console.log("Setting team doc at", teamRef.path, teamDocPayload);
      try {
        await setDoc(teamRef, teamDocPayload, { merge: false });
      } catch (e) {
        await logError({
          leagueID,
          userID: currentUser?.uid,
          action: "handleJoin_setTeamDoc",
          payload: { teamDocPayload },
          error: e
        });
        throw e;
      }

      if (!currentGWSnapshot.empty) {
        // const currentGWDoc = currentGWSnapshot.docs[0];
        // const currentGWID = currentGWDoc.id;

        // Seed a basic snapshot doc under /leagues/{leagueID}/seasons/{seasonID}/gameweeks/{currentGWID}/teams/{teamID}
        const gwTeamsRef = collection(db, "leagues", leagueID, "seasons", seasonID, "gameweeks", currentGWID, "teams");
        const teamGameweekRef = doc(gwTeamsRef, teamID);
        // Use clean schema for GW snapshot (admin docs style)
        const gwSnapshotPayload = {
          leagueID,
          teamID,
          managerID: currentUser.uid,

          startGW: currentGWID,
          lastSavedAt: serverTimestamp(),

          selections: {},
          captains: {},
          chipsUsed: {},

          transfersFree: { [currentGWID]: "unlimited" },

          bankStart: { [currentGWID]: startingBudget },
          currentBudget: { [currentGWID]: startingBudget },

          squadMeta: {
            [currentGWID]: {
              DEF: {},
              MID: {},
              FWD: {}
            }
          }
        };
        console.log("Setting GW snapshot at", teamGameweekRef.path, gwSnapshotPayload);
        try {
          await setDoc(teamGameweekRef, gwSnapshotPayload, { merge: false });
        } catch (e) {
          await logError({
            leagueID,
            userID: currentUser?.uid,
            action: "handleJoin_seedGWSnapshot",
            payload: { gwSnapshotPayload },
            error: e
          });
          throw e;
        }
      }

      // Update user doc: merge roles map, teams, startGW, and leagueIDs
      const userRef = paths.user(currentUser.uid);

      // Read current user doc to merge roles + resolve a stable display name for analytics
      let existingRoles = {};
      let resolvedUserName = currentUser?.displayName || currentUser?.email || "";
      try {
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          if (data && typeof data.roles === "object" && data.roles !== null) {
            existingRoles = { ...data.roles };
          }
          if (data?.displayName) {
            resolvedUserName = data.displayName;
          }
        }
      } catch (e) {
        console.error("Failed to read user doc:", e);
      }
      const mergedRoles = { ...existingRoles, [leagueID]: "user" };
      const userDocPayload = {
        leagueIDs: arrayUnion(leagueID),
        teams: { [leagueID]: teamID },
        startGW: { [leagueID]: currentGWID },
        currentSeasonID: { [leagueID]: leagueData.currentSeasonID },
        roles: mergedRoles,
      };
      // Write with merge: true to preserve other fields, but always set teams.{leagueID} to currentUser.uid
      await setDoc(
        userRef,
        userDocPayload,
        { merge: true }
      );
      console.log("User doc updated at", userRef.path, "with payload:", userDocPayload);

      // Usage tracking: join completed
      // IMPORTANT: do not block the user if logging fails
      try {
        await logEvent({
          eventType: "user_joined_league",
          leagueID,
          userID: currentUser.uid,
          userName: resolvedUserName || "",
          role: mergedRoles?.[leagueID] || "user",
          source: "join_league",
          url: window.location.href,
          metadata: {
            seasonID,
            teamID,
            teamName: teamName.trim() || "",
            currentGWID: currentGWID || "",
            joinMethod: "code",
            teamCreated: true
          }
        });
      } catch (e) {
        // swallow logging errors
        console.warn("logEvent user_joined_league failed:", e);
      }

      // Removed playerCount update as per instructions

      showToast("League joined!", "success");

      // Fetch players for the claim step
      try {
        const playersSnap = await getDocs(paths.leaguePlayers(leagueID));
        const playersList = playersSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setLeaguePlayers(playersList);
      } catch (_) {}

      setJoinedLeagueID(leagueID);
      setStep("claim");
    } catch (err) {
      console.error("Join league failed:", err);
      await logError({
        leagueID: code || "N/A",
        userID: currentUser?.uid || "unknown",
        action: "handleJoin_main",
        payload: { code, teamName },
        error: err
      });
      showToast("An error occurred while joining the league.", "error");
    }
  };

  const handleSaveClaim = async () => {
    if (!currentUser || !joinedLeagueID) return;
    setSavingClaim(true);
    try {
      if (selectedPlayerID) {
        const userRef = paths.user(currentUser.uid);
        await setDoc(userRef, {
          linkedPlayerIDs: { [joinedLeagueID]: selectedPlayerID }
        }, { merge: true });
      }
    } catch (err) {
      console.error("Failed to save player claim:", err);
    } finally {
      setSavingClaim(false);
      navigate(`/pickteam/${joinedLeagueID}`);
    }
  };

  return (
    <Layout>
      <Container>
        <FormPanel>
          {step === "join" ? (
            <>
              <h2 className="text-2xl font-bold text-center mb-6">Join a League</h2>
              {!currentUser && (
                <p className="text-red-600 text-center mb-4 font-semibold">
                  You must be signed in to join a league.
                </p>
              )}
              <StyledForm onSubmit={(e) => e.preventDefault()}>
                <div>
                  <StyledLabel>League Code</StyledLabel>
                  <StyledInput
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Enter league code"
                    disabled={!currentUser}
                  />
                </div>
                <div>
                  <StyledLabel>Fantasy Team Name</StyledLabel>
                  <StyledInput
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="Name your fantasy team"
                    disabled={!currentUser}
                  />
                </div>
                <Button
                  type="button"
                  variant="pink"
                  onClick={handleJoin}
                  disabled={!currentUser}
                >
                  Join
                </Button>
                <div className="text-center">
                  <Button
                    type="button"
                    variant="blue"
                    onClick={() => navigate(-1)}
                    className="w-full"
                  >
                    ← Back
                  </Button>
                </div>
              </StyledForm>
            </>
          ) : (
            <>
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <div style={{ fontSize: "2rem", marginBottom: 8 }}>⚽</div>
                <h2 style={{ margin: "0 0 8px", fontWeight: 700, fontSize: "1.3rem" }}>
                  You're in!
                </h2>
                <p style={{ margin: 0, color: "#666", fontSize: "0.9rem" }}>
                  Who are you in real life? This helps highlight your teammates when picking your fantasy team.
                </p>
              </div>

              {leaguePlayers.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                  {leaguePlayers.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedPlayerID(
                        selectedPlayerID === p.id ? "" : p.id
                      )}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 14px",
                        borderRadius: 10,
                        border: selectedPlayerID === p.id
                          ? "2px solid #50B4C8"
                          : "1px solid #e5e7eb",
                        background: selectedPlayerID === p.id ? "#f0fafd" : "#fff",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <span style={{ fontWeight: 600, fontSize: "0.95rem", color: "#234" }}>
                        {p.name}
                      </span>
                      <span style={{ fontSize: "0.82rem", color: "#888" }}>
                        {p.position}
                        {selectedPlayerID === p.id && (
                          <span style={{ marginLeft: 8, color: "#50B4C8", fontWeight: 700 }}>✓</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p style={{ color: "#888", fontSize: "0.9rem", textAlign: "center", marginBottom: 20 }}>
                  No players found in this league yet.
                </p>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Button
                  type="button"
                  variant="pink"
                  onClick={handleSaveClaim}
                  disabled={savingClaim || !selectedPlayerID}
                >
                  {savingClaim ? "Saving..." : "That's me →"}
                </Button>
                <button
                  type="button"
                  onClick={() => navigate(`/pickteam/${joinedLeagueID}`)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#888",
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontFamily: "inherit",
                    padding: "4px 0",
                  }}
                >
                  Skip for now
                </button>
              </div>
            </>
          )}
        </FormPanel>
      </Container>
    </Layout>
  );
}